import React, { useState } from 'react';
import {
  AppProvider, Frame, Navigation, TopBar, Page,
  Text,
} from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import {
  HomeIcon, StoreIcon, ProfileIcon,
} from '@shopify/polaris-icons';
import { useAdminAuth, useAdminStats, useAdminShops } from '../../hooks/useAdmin';
import { AdminLogin } from './AdminLogin';
import { AdminStatsCards } from './AdminStatsCards';
import { AdminShopsTable } from './AdminShopsTable';
import { AdminUsersPanel } from './AdminUsersPanel';

type Tab = 'dashboard' | 'shops' | 'users';

function AdminNav({ tab, onTab }: { tab: Tab; onTab: (t: Tab) => void; onLogout: () => void; adminName: string }) {
  return (
    <Navigation location={`/${tab}`}>
      <Navigation.Section
        items={[
          { url: '#', label: 'Dashboard', icon: HomeIcon,    selected: tab === 'dashboard', onClick: () => onTab('dashboard') },
          { url: '#', label: 'Shops',     icon: StoreIcon,   selected: tab === 'shops',     onClick: () => onTab('shops') },
          { url: '#', label: 'Admins',    icon: ProfileIcon, selected: tab === 'users',     onClick: () => onTab('users') },
        ]}
      />
      <Navigation.Section
        fill
        items={[]}
      />
    </Navigation>
  );
}

function DashboardTab() {
  const { stats, loading, error } = useAdminStats();
  return (
    <Page title="Platform Overview">
      <AdminStatsCards stats={stats} loading={loading} error={error} />
    </Page>
  );
}

function ShopsTab() {
  const { shops, loading, error, actionId, page, lastPage, total, search, setPage, setSearch, suspend, reactivate } = useAdminShops();
  return (
    <Page title="Shops">
      <AdminShopsTable
        shops={shops}
        loading={loading}
        error={error}
        actionId={actionId}
        page={page}
        lastPage={lastPage}
        total={total}
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        onPage={setPage}
        onSuspend={suspend}
        onReactivate={reactivate}
      />
    </Page>
  );
}

function UsersTab({ adminId }: { adminId: number }) {
  return (
    <Page title="Admin Users">
      <AdminUsersPanel currentAdminId={adminId} />
    </Page>
  );
}

export function AdminApp() {
  const { admin, loading, error, login, logout } = useAdminAuth();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [mobileNav, setMobileNav] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  if (loading) {
    return (
      <AppProvider i18n={enTranslations}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <Text as="p" variant="bodyMd">Loading…</Text>
        </div>
      </AppProvider>
    );
  }

  if (!admin) {
    return <AdminLogin onLogin={login} loading={loading} error={error} />;
  }

  const topBar = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={() => setMobileNav((v) => !v)}
      userMenu={
        <TopBar.UserMenu
          actions={[{ items: [{ content: 'Sign out', onAction: logout }] }]}
          name={admin.name}
          detail={admin.role_label}
          initials={admin.name.charAt(0).toUpperCase()}
          open={userMenuOpen}
          onToggle={() => setUserMenuOpen((v) => !v)}
        />
      }
    />
  );

  const nav = (
    <AdminNav
      tab={tab}
      onTab={setTab}
      onLogout={logout}
      adminName={admin.name}
    />
  );

  let content: React.ReactNode;
  if (tab === 'dashboard') content = <DashboardTab />;
  else if (tab === 'shops') content = <ShopsTab />;
  else content = <UsersTab adminId={admin.id} />;

  return (
    <AppProvider i18n={enTranslations}>
      <Frame
        topBar={topBar}
        navigation={nav}
        showMobileNavigation={mobileNav}
        onNavigationDismiss={() => setMobileNav(false)}
      >
        {content}
      </Frame>
    </AppProvider>
  );
}
